import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

// Typed wrapper over the activity feed (lobbero_be/src/CRUD/activity.js, mounted
// at app.use('/api', activityRoutes), so every path is /api/activity*).
//
// Uses the DEFAULT axios instance, like every other client here, so it inherits
// the interceptors from axios-setup.ts: withCredentials for the httpOnly session
// cookies, the X-CSRF-Token double-submit header, and the single-flight
// 401 -> refresh -> retry. There is no auth code in this file and must not be.
//
// buildingId IS A FILTER, NOT THE BOUNDARY. Omitting it asks for every building
// the caller can see — which is the admin view — and RLS decides what that means.
// A caller cannot widen their own answer by dropping it.

/** The four books the desk keeps. Derived server-side (activity/feed.js) so the
 *  grouping cannot drift between the desk panel and the admin view. */
export type ActivityGroup =
  | 'residents'
  | 'visitors'
  | 'parcels'
  | 'daily_log'
  /** A correction made to one of the four above. Its own group rather than a
   *  flag on them, because it is not a movement: it carries no direction and it
   *  is excluded from the In / Out / Still-inside tiles by the server. */
  | 'corrections';

/** Which way the person or parcel went. Null for a daily-log note, which is an
 *  observation about the building and has no direction — saying 'in' there would
 *  be inventing a fact. */
export type ActivityDirection = 'in' | 'out' | null;

export interface ActivityActor {
  id: string;
  /** Decrypted server-side. Null when the stored ciphertext no longer opens — a
   *  name we cannot read is shown as unknown, never as the raw token. */
  name: string | null;
  role: string | null;
}

export interface ActivityEntry {
  kind: string;
  source: 'visitor_log' | 'daily_log' | 'package';
  group: ActivityGroup;
  groupLabel: string;
  direction: ActivityDirection;
  at: string;
  /** The row's last write. Optional: an older server omits it. The handover's
   *  full log compares it to the moment the handover was written. */
  updatedAt?: string | null;
  severity: string;
  unit: string | null;
  people: string[];
  headcount: number | null;
  priority: string | null;
  /** The SECOND classifier, different per source: a parcel's carrier (Temu,
   *  DHL), a visitor's chosen detail. It is the first thing a reader uses to
   *  recognise a row — "the Temu one" — and it was not on the wire. */
  subKind: string | null;
  /** A multi-parcel drop is ONE row with a count. Without this, three parcels in
   *  one delivery read as one parcel. Null for anything that is not a parcel. */
  parcelCount: number | null;
  text: string[];
  /** A PARCEL'S TWO NAMES, LABELLED — `people` above is unlabelled and
   *  de-duplicated, so it cannot say which name is the addressee and which
   *  collected it, and when they are the same person it collapses to one and
   *  the record stops mentioning the handover at all. Null on every other
   *  source; `parcelCollector` is also null on a parcel ARRIVAL, which means
   *  "still on the shelf" rather than "not recorded". */
  parcelRecipient?: string | null;
  parcelCollector?: string | null;
  /** How sure we are the collector is who they said: 'resident' / 'occupant'
   *  matched a real person on the unit, 'typed' / 'no_match' did not. A parcel
   *  released to somebody who is not a resident is exactly the case the owner
   *  asked to be able to see — "which user or NON USER picked up the parcel". */
  parcelCollectorResolution?: string | null;
  /** WHICH of a parcel's three events this row is: 'arrival', 'handover'
   *  (somebody collected it) or 'return' (it went back to the carrier).
   *
   *  IT CANNOT BE DERIVED FROM THE OTHER FIELDS. Both departures carry
   *  `direction: 'out'` and `kind: 'Parcel out'` — correctly, because the parcel
   *  left either way — so this is the only thing that separates "4B took it"
   *  from "it went back to DHL". Null on every non-parcel source. */
  parcelEvent?: 'arrival' | 'handover' | 'return' | null;
  /** "Still inside": an arrival with no departure recorded. Null for sources
   *  where the question does not apply. */
  open: boolean | null;
  departedAt: string | null;
  /** 'desk' = a human watched it and is named. 'auto'/'backfill' = housekeeping,
   *  and NEVER an observation of anybody leaving. The database guarantees the
   *  distinction (chk_vl_exit_machine / chk_dl_close_machine), so a screen may
   *  branch on this without re-deriving it — and must, or the nightly sweep's
   *  end-of-day stamp renders as a departure somebody witnessed. */
  exitSource: string | null;
  actor: ActivityActor | null;
  /** What a deep link needs — and deliberately NOT the 'v:24' style ref token,
   *  which is a database key wearing a prefix. */
  ref: { source: string; id: number | string };
  /** The building the row belongs to, and that building's OWN zone — for the
   *  second line of a record ("14:05 in Toronto") and for the desk, which shows
   *  building time to everybody. */
  buildingId?: number | null;
  tz?: string | null;
}

export interface ActivityGroupCount {
  key: ActivityGroup;
  label: string;
  count: number;
}

export interface ActivityFeedResponse {
  entries: ActivityEntry[];
  /** Per-tab totals with THE TAB FILTER ITSELF LIFTED — "how many rows would I
   *  see if I clicked this tab", every other filter still applied. Counting them
   *  inside the fully-filtered set instead made every unselected tab read 0. */
  groups: ActivityGroupCount[];
  /** The "All" tab's count: the five tabs' set, group filter cleared. NOT the
   *  same number as `total` once a tab is selected — `total` is the denominator
   *  of "showing 25 of N" and answers to every filter including the tab. */
  groupTotal: number;
  total: number;
  /** True when the scan hit its ceiling, so `total` and the group counts are a
   *  FLOOR. Render "500+" rather than presenting them as exact. */
  totalCapped: boolean;
  hasMore: boolean;
  /** Arrivals in the window with no departure recorded — the same "still inside"
   *  state the handover reports as a follow-up, from the same stream, so the two
   *  cannot disagree about how many people the building thinks are in it. */
  openCount: number;
  inCount: number;
  outCount: number;
  /** The Entries tile: direction and status both lifted, since that tile is the
   *  control that clears exactly those two. Equal to `total` when neither is
   *  set. */
  moveTotal: number;
  /** `zones` = the buildings' OWN zones present in the answer. */
  window: { from: string; to: string; tz: string; zones?: string[] };
  /** THE CLOCK THIS ANSWER IS ON, and the zone a screen formats it in — unless
   *  the screen is a building-clock surface (the desk), which formats in each
   *  entry's own `tz`. */
  tz?: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
}

export interface ActivityQuery {
  buildingId?: number | null;
  group?: ActivityGroup[];
  direction?: 'in' | 'out';
  kind?: string[];
  actor?: string[];
  unit?: string;
  person?: string;
  status?: 'open' | 'closed';
  day?: string;
  /** A date RANGE, both YYYY-MM-DD and both INCLUSIVE. The server resolves each
   *  end into a whole desk day in the building's zone, so "1st to 3rd" is the
   *  whole of the 1st through the whole of the 3rd — not two UTC midnights,
   *  which would drop the last evening for any building west of Greenwich.
   *  Either end alone is valid. Composes with every other filter here. */
  dateFrom?: string;
  dateTo?: string;
  /* NO ZONE HERE. The window is cut on the clock of whoever asks (R-T12): the
     building's for a concierge, the device's for an owner or a manager. The
     server learns the device's zone from the X-Client-Timezone header
     (axios-setup.ts) and says which clock it used (`tz`, `tz_source`). */
  /** Free-text search across EVERYTHING on the row — people, kind, group,
   *  direction, unit, priority, who logged it, and the note. Every whitespace
   *  separated term must match, so a second word narrows. Bounded by the same
   *  window and the same RLS as everything else — a search cannot reach outside
   *  the buildings the caller can already see. */
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ActivityEdit {
  field: string;
  previous: string | null;
  /** False means the stored value could not be decrypted. Show "previous value
   *  unavailable"; never show the token. */
  previousReadable: boolean;
  current: string | null;
  currentReadable: boolean;
  fromSource: string | null;
  toSource: string | null;
  at: string;
  actor: ActivityActor | null;
}

export interface ActivityAction {
  action: string;
  at: string;
  actor: ActivityActor | null;
  /** Field NAMES only — audit_log does not carry values for an update. */
  changedFields: string[] | null;
}

export interface ActivityHistoryResponse {
  edits: ActivityEdit[];
  actions: ActivityAction[];
  edited: boolean;
}

const params = (query: ActivityQuery): Record<string, string> => {
  const out: Record<string, string> = {};
  const put = (key: string, value: unknown) => {
    if (value == null || value === '') return;
    out[key] = Array.isArray(value) ? value.join(',') : String(value);
  };

  put('buildingId', query.buildingId);
  put('group', query.group);
  put('direction', query.direction);
  put('kind', query.kind);
  put('actor', query.actor);
  put('unit', query.unit);
  put('person', query.person);
  put('status', query.status);
  put('day', query.day);
  put('dateFrom', query.dateFrom);
  put('dateTo', query.dateTo);
  put('q', query.q);
  put('limit', query.limit);
  put('offset', query.offset);

  return out;
};

/**
 * ABORT SUPPORT. Additive: every existing caller passes nothing.
 *
 * The Dashboard aborts a request the moment its question changes. This
 * route runs on the caller's RLS transaction like every other, so an abandoned
 * one holds a pooled connection for the life of a query nobody is waiting for.
 */
export interface ActivityRequestOptions {
  signal?: AbortSignal;
}

export const fetchActivity = async (
  query: ActivityQuery = {},
  opts: ActivityRequestOptions = {}
): Promise<ActivityFeedResponse> => {
  const res = await axios.get(`${apiRoute}/activity`, {
    params: params(query),
    signal: opts.signal,
  });
  // A SUCCESS WITHOUT ROWS IS NOT A FEED. The server never answers 200 without
  // `entries`, yet production Firefox once handed the desk's feed exactly that
  // (2026-10-14, cause in the browser not found), and the screen fell over on
  // `data.entries.find` behind the error boundary. Refused here, it becomes the
  // feed's own "could not load activity" — and the warning keeps what arrived,
  // so the next occurrence names its cause.
  if (!res.data || !Array.isArray((res.data as ActivityFeedResponse).entries)) {
    // eslint-disable-next-line no-console
    console.warn(
      '[Lobbero] activity: an answer without entries',
      res.status,
      typeof res.data
    );
    throw new Error('activity: an answer without entries');
  }

  return res.data as ActivityFeedResponse;
};

/** The operational fields a person may correct from the record view. Names are
 *  deliberately absent: they carry provenance and an occupancy resolution that
 *  the desk capture flow owns, and a second editor for them would drift. */
export type ActivityEditable = Record<string, string | number | boolean | null>;

export interface ActivityRecordResponse {
  /** Null for any record the caller may read but not correct through this
   *  surface. Parcels USED to be null unconditionally — the server had no
   *  editable-field list for them, so the one record type with a full PUT route
   *  behind it was the one the editor never offered. Owner report, 2026-08-14.
   *
   *  `released_to` is present only on a parcel that has actually been handed
   *  over: on one still on the shelf there is no collection to correct, and an
   *  editable box whose value the route would ignore is worse than no box. */
  editable: ActivityEditable | null;
  /** A parcel is TWO events of one row (arrival and handover), so this is a
   *  list. Newest first, so a collected parcel opens on the collection. */
  events: ActivityEntry[];
  /** A visitor arrival signed in on a visitor pass (resident-portal Plan 3);
   *  null (or absent, from an older server) otherwise. */
  pass?: ActivityPassLine | null;
  /** THE CLOCK THIS RECORD IS ANSWERED ON (R-T12), and the record's BUILDING
   *  with that building's own zone — the two lines of the detail: "20:05 your
   *  time · 14:05 in Toronto". `building` is null for a record with none. */
  tz?: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
  building?: { id: number; tz: string } | null;
}

/** "On a pass added by the building" / "On a resident pass", and where the
 *  sign-in fell against the window. `pass_id` is null when the pass has since
 *  been removed by retention — the arrival still came on one. */
export interface ActivityPassLine {
  on_pass: boolean;
  pass_id: string | null;
  window_state: 'in_window' | 'early' | 'late' | null;
  created_via: 'resident' | 'staff' | null;
}

export const fetchActivityRecord = async (
  source: string,
  id: number | string
): Promise<ActivityRecordResponse> => {
  const { data } = await axios.get(
    `${apiRoute}/activity/${encodeURIComponent(source)}/${encodeURIComponent(String(id))}`
  );

  return data;
};

/** Writes through the EXISTING log endpoints — no second write path. They are
 *  permission-gated (`dailylog:create` / `visitorlog:create` / `package:edit`),
 *  they accept a partial body, and they already append to entry_field_edits and
 *  entry_versions, so an edit made here shows up in this record's own history
 *  and in the org-wide corrections log without any extra work.
 *
 *  PUT /packages/:id was always here; it simply had no caller from this screen.
 *  Its body speaks the packages table's own column names (`unit_label` rather
 *  than `apartmentLocation`), which is why the editable payload the server
 *  sends for a parcel uses those names too — the patch goes back exactly as it
 *  came. */
export const updateActivityRecord = async (
  source: 'daily_log' | 'visitor_log' | 'package',
  id: number | string,
  patch: ActivityEditable
): Promise<void> => {
  const path =
    source === 'daily_log'
      ? 'daily-log'
      : source === 'package'
        ? 'packages'
        : 'visitor-log';
  await axios.put(`${apiRoute}/${path}/${encodeURIComponent(String(id))}`, patch);
};

/** ═════════ EVERY CORRECTION IN THE ORGANISATION ═════════
 *
 *  `fetchActivityHistory` below answers "what happened to THIS record", which a
 *  reader can only ask once they already suspect the record. This answers "show
 *  me every correction", which nothing could ask before it existed.
 *
 *  Scoped by RLS on entry_versions, so there is no organisation parameter and
 *  no role branch: a caller gets their own organisation's corrections and
 *  nothing else. */
export interface CorrectionChange {
  field: string;
  previous: string | null;
  previousReadable: boolean;
  current: string | null;
  currentReadable: boolean;
}

export interface CorrectionEntry {
  id: number;
  /** The record that was corrected — `package`, `visitor_log` or `daily_log`,
   *  ready to hand straight to the record modal. */
  recordSource: string;
  recordId: string;
  version: number;
  at: string;
  actor: { id: string; name: string | null; role: string | null } | null;
  /** Null when the corrected record has since been deleted: there is a previous
   *  state and nothing left to compare it against. The trail outlives the
   *  record on purpose. */
  changes: CorrectionChange[] | null;
}

export interface CorrectionsResponse {
  corrections: CorrectionEntry[];
  hasMore: boolean;
}

export const fetchCorrections = async (
  params: { limit?: number; offset?: number; table?: string } = {},
  opts: ActivityRequestOptions = {}
): Promise<CorrectionsResponse> => {
  const { data } = await axios.get<CorrectionsResponse>(
    `${apiRoute}/activity/corrections`,
    { params, signal: opts.signal }
  );

  return data;
};

export const fetchActivityHistory = async (
  source: string,
  id: number | string
): Promise<ActivityHistoryResponse> => {
  const { data } = await axios.get(
    `${apiRoute}/activity/${encodeURIComponent(source)}/${encodeURIComponent(
      String(id)
    )}/history`
  );

  return data;
};
